import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { IIncidentCloseDTO, IIncidentDto } from '../../interfaces/incident';
import { IIncidentFilter } from '../../interfaces/incidents-page-filter';
import { IJobScreen } from '../../interfaces/job/job';
import { IPageResult } from '../../interfaces/page-result';
import { IncidentsService } from '../../services/incidents.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SelectItem } from 'primeng/api';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { OverlayPanel } from 'primeng/overlaypanel';
import { BBWTMessageService } from '../../services/message.service';
import { UserService } from '../../services/user.service';
import moment from 'moment';

import { EventService } from '../../services';

@Component({
  selector: 'job-screen-incidents',
  templateUrl: './job-screen-incidents.component.html',
  styleUrls: ['./job-screen-incidents.component.scss'],
})
export class JobScreenIncidentsComponent implements OnInit, OnDestroy {
  @Input() set selectedTask(value: any) {
    this._selectedTask = value;
  }
  private _selectedTask: any;

  @Input() set jobId(value: number) {
    this._jobId = value;
    this.clearFilter();
  }
  private _jobId: number;

  @Input() set jobInfo(value: IJobScreen) {
    this._jobInfo = value;
  }
  private _jobInfo: IJobScreen;

  loading: boolean;
  page: IPageResult<IIncidentDto> = { items: [], total: 0 };
  filter: IIncidentFilter;
  private loadSubject: Subject<IIncidentFilter>;

  raiseIncidentsForm: UntypedFormGroup;
  typeOpts: SelectItem[] = [];

  closeIncidentDialog: boolean = false;
  closeIncidentForm: UntypedFormGroup;

  // incident item selected for closure
  private closeItem: IIncidentDto;
  closureReasons: SelectItem[] = [];

  public currentUserId: string;

  refreshIncidentsSub: Subscription;

  constructor(
    private incidentsService: IncidentsService,
    private formBuilder: UntypedFormBuilder,
    private messageService: BBWTMessageService,
    private userService: UserService,
    private eventService: EventService,
  ) {
    this.loadSubject = new Subject<IIncidentFilter>();
    this.loadSubject
      .asObservable()
      .pipe(debounceTime(500))
      .subscribe((f) => {
        this.loading = true;
        this.incidentsService.getPage(f).then((res) => {
          this.page = res;
          this.loading = false;
        });
      });

    this.raiseIncidentsForm = formBuilder.group({
      typeId: ['', Validators.required],
      comment: [''],
    });

    this.filter = this._getDefaultFilter();

    this.closeIncidentForm = formBuilder.group({
      closureReasonId: ['', Validators.required],
      closureNotes: ['', [Validators.maxLength(1024)]],
    });
  }

  ngOnInit(): void {
    this.refreshIncidentsSub = this.eventService.RefreshIncidentsEvent.subscribe((x) => {
      this.search();
    });

    this.incidentsService.getCustomTypes().then((res) => {
      this.typeOpts = res;
      this.search();
    });

    this.incidentsService.getClosureReasonsForDropdown().then((res) => {
      this.closureReasons = res;
    });

    this.currentUserId = this.userService.currentUser.id;
  }

  ngOnDestroy() {
    if (this.refreshIncidentsSub) {
      this.refreshIncidentsSub.unsubscribe();
    }
  }

  onGridLazyLoad(event: any) {
    this.filter.first = event.first;
    this.filter.rows = event.rows;
    this.filter.sortField = event.sortField;
    this.filter.sortOrder = event.sortOrder;
    this.search();
  }

  getDateDescription(date: Date): string {
    return date ? moment(date).format("DD MMM 'YY HH:mm") : '';
    //return date ? moment.duration(moment(date).diff(moment())).humanize(true) : 'N/A';
  }

  closeIncident(item: IIncidentDto) {
    // show confirmation/closure reason dialog
    this.closeItem = item;
    this.closeIncidentDialog = true;
  }

  confirmCloseIncident() {
    let closeDto: IIncidentCloseDTO = this.closeIncidentForm.value;
    closeDto.id = this.closeItem.id;
    closeDto.typeId = this.closeItem.typeId;
    closeDto.typeName = this.closeItem.typeName;
    closeDto.jobId = this.closeItem.jobId;
    closeDto.taskId = this.closeItem.taskId;
    closeDto.jobNumber = this.closeItem.jobNumber;
    closeDto.vehicleReg = this.closeItem.vehicleReg;

    //console.log("close DTO: ", closeDto);

    this.incidentsService.close(closeDto).then((res) => {
      this.closeItem.closed = res;
      this.clearClosureVars();
      this.search();
    });
  }

  clearClosureVars() {
    this.closeItem = null;
    this.closeIncidentForm.reset();
    this.closeIncidentDialog = false;
  }

  search() {
    if (!this.filter) {
      this.filter = this._getDefaultFilter();
    }
    this.loadSubject.next(this.filter);
  }

  clearFilter() {
    this.filter = this._getDefaultFilter();
    this.search();
  }

  @ViewChild('assigneeSelectionPanel') assigneeSelectionPanel: OverlayPanel;
  private _currentIncidentForAssignment: IIncidentDto;

  openAssigneeSelectionPanel(event: any, item: IIncidentDto) {
    this._currentIncidentForAssignment = item;
    this.assigneeSelectionPanel.toggle(event);
  }

  confirmAssigneeSelection() {
    this.assignToMe(this._currentIncidentForAssignment);
    this.assigneeSelectionPanel.hide();
  }

  onAssigneeSelectionPanelHide() {
    this._currentIncidentForAssignment = null;
  }

  assignToMe(item: IIncidentDto) {
    this.incidentsService.assigneeToMe(item.id).then(() => this.search());
  }

  getRowStyle(rowData: any, index: number) {
    return !rowData.assigneeId ? 'grid-row-bold' : '';
  }

  raiseIncident() {
    // if selected task is null, set to first task in first order
    let taskId = this._jobInfo.orders[0].tasks[0];

    if (this._selectedTask) {
      taskId = this._selectedTask.id;
    }

    const data = { jobId: this._jobInfo.id, taskId: taskId, ...this.raiseIncidentsForm.value };
    this.incidentsService.raiseJobIncident(data).then(() => {
      this.search();
    });
    this.raiseIncidentsForm.reset();
  }

  @Output() onShowHistory: EventEmitter<any> = new EventEmitter<any>();
  showHistory() {
    this.onShowHistory.emit();
  }

  private _getDefaultFilter(): IIncidentFilter {
    return {
      first: 0,
      rows: 20,
      sortField: '',
      sortOrder: 1,
      filters: {
        jobId: this._jobId,
      },
    };
  }
}
